import React from 'react';
import { Card } from '../components/ui/card';
import { Crown, Scale, TrendingUp, AlertTriangle, Globe, Monitor, Calendar, Gavel, MapPin, Bell, ShieldCheck, Briefcase } from 'lucide-react';
import { mockData } from '../data/mockData';

const iconMap = {
  Crown: Crown,
  Scale: Scale,
  TrendingUp: TrendingUp,
  AlertTriangle: AlertTriangle,
  Globe: Globe,
  Monitor: Monitor,
  Calendar: Calendar,
  Gavel: Gavel,
  MapPin: MapPin,
  Bell: Bell,
  ShieldCheck: ShieldCheck,
  Briefcase: Briefcase
};

const BenefitsSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Benefits of Federal Trademark Registration
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Discover why thousands of businesses choose federal trademark registration to protect their brand identity, enforce their rights, and build long-term value.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {mockData.benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <Card
                  key={index}
                  className="p-6 bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-blue-100 leading-relaxed mb-3">
                    {benefit.description}
                  </p>
                  {benefit.details && (
                    <p className="text-sm text-blue-200 leading-relaxed border-t border-white/20 pt-3 mt-3">
                      {benefit.details}
                    </p>
                  )}
                </Card>
              );
            })}
          </div>

          <div className="mt-16 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Protect Your Most Valuable Asset
              </h3>
              <p className="text-lg text-blue-100 mb-8 max-w-3xl mx-auto">
                Federal trademark registration is an investment in your business's future success and protection.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400 mb-2">Nationwide</div>
                  <p className="text-blue-100 text-sm">All 50 states coverage</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400 mb-2">Perpetual</div>
                  <p className="text-blue-100 text-sm">Renewable indefinitely</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400 mb-2">Valuable</div>
                  <p className="text-blue-100 text-sm">Transferable asset</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h4 className="text-xl font-bold mb-3">Why Federal vs. State Registration?</h4>
              <ul className="space-y-2 text-blue-100">
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Federal registration provides nationwide protection, not limited to one state</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Access to federal courts for enforcement and damages</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Required for international protection through Madrid Protocol</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Customs recordation to stop counterfeit imports</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h4 className="text-xl font-bold mb-3">Long-Term Business Advantages</h4>
              <ul className="space-y-2 text-blue-100">
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Increases company valuation for investors and acquisitions</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Creates licensing and franchising opportunities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Builds consumer trust and brand recognition</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-400 mt-1">•</span>
                  <span>Protects against domain squatters and cybersquatting</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
